import React, { lazy, Suspense, Component, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as Sentry from "@sentry/react";

class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Caught error:", error);

    // Auto-reload once on chunk load failures (stale cache after deploy)
    const isChunkError =
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.name === 'ChunkLoadError';

    if (isChunkError && !sessionStorage.getItem('chunk_reload')) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
      return;
    }

    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-background text-foreground p-4">
          <h1 className="text-2xl text-[#cfef00] mb-4 text-center">Something went wrong</h1>
          <p className="text-muted-foreground mb-6 text-center max-w-md">
            Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-[#cfef00] text-black font-semibold rounded-full hover:bg-[#b8d900] transition-colors"
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ReactGA from "react-ga4";
import { ThemeProvider } from 'next-themes';
import { AuthProvider } from '@/contexts/AuthContext';
import { LocationProvider } from '@/contexts/LocationContext';
import { TourProvider } from '@/contexts/TourContext';
import { FeedProvider } from '@/contexts/FeedContext';
import { DishSheetProvider } from '@/contexts/DishSheetContext';
import { DishStoryProvider } from '@/contexts/DishStoryContext';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster, toast } from '@/components/ui/sonner';
import RouteChangeTracker from '@/components/analytics/RouteChangeTracker';
import AppShell from '@/components/layout/AppShell';
import NotFound from '@/pages/NotFound';
import './App.css';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/integrations/supabase/client';

// Auth pages - keep static for fast initial load on entry points
import SignUp from '@/pages/SignUp';
import LogIn from '@/pages/LogIn';
import ForgotPassword from '@/pages/ForgotPassword';
import UpdatePassword from '@/pages/UpdatePassword';
import AuthCallback from '@/pages/AuthCallback';

// Lazy-loaded page components for code splitting
const Boards = lazy(() => import('@/pages/Boards'));
const Profile = lazy(() => import('@/pages/Profile'));
const ProfileRedirect = lazy(() => import('@/pages/ProfileRedirect'));
const DishGalleryPage = lazy(() => import('@/pages/DishGalleryPage'));
const GlobalTop100 = lazy(() => import('@/pages/GlobalTop100'));
const Drools = lazy(() => import('@/pages/Drools'));
const Snaps = lazy(() => import('@/pages/Snaps'));
const FeedPage = lazy(() => import('@/pages/FeedPage'));
const Pics = lazy(() => import('@/pages/Pics'));
const Drool = lazy(() => import('@/pages/Drool'));
const AccountSettings = lazy(() => import('@/pages/AccountSettings'));
const DripsPage = lazy(() => import('@/pages/DripsPage'));
const RestaurantPage = lazy(() => import('@/pages/RestaurantPage'));

// Initialize Google Analytics 4
const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
if (import.meta.env.PROD && gaMeasurementId) {
  ReactGA.initialize(gaMeasurementId);
  console.log("[Analytics] GA4 Initialized");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute - data is fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
      refetchOnWindowFocus: false, // Don't refetch on tab switch
      retry: 1, // Only retry once on failure
    },
  },
});

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.2, ease: 'easeInOut' }}
    style={{ willChange: 'opacity, transform' }}
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Routes without the main app shell */}
        <Route path="/signup" element={<PageWrapper><SignUp /></PageWrapper>} />
        <Route path="/login" element={<PageWrapper><LogIn /></PageWrapper>} />
        <Route path="/forgot-password" element={<PageWrapper><ForgotPassword /></PageWrapper>} />
        <Route path="/update-password" element={<PageWrapper><UpdatePassword /></PageWrapper>} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Routes with the main app shell */}
        <Route path="/" element={<AppShell />}>
          <Route index element={<Navigate to="/boards" replace />} />
          <Route path="boards" element={<PageWrapper><Boards /></PageWrapper>} />
          <Route path="profile" element={<PageWrapper><ProfileRedirect /></PageWrapper>} />
          <Route path="profile/:userId" element={<PageWrapper><Profile /></PageWrapper>} />
          <Route path="dish/:id" element={<PageWrapper><DishGalleryPage /></PageWrapper>} />
          <Route path="restaurant/:id" element={<PageWrapper><RestaurantPage /></PageWrapper>} />
          <Route path="whats-new" element={<Navigate to="/boards?tab=whats-new" replace />} />
          <Route path="lists" element={<Navigate to="/boards?tab=lists" replace />} />
          <Route path="global-100" element={<PageWrapper><GlobalTop100 /></PageWrapper>} />
          <Route path="drools" element={<PageWrapper><Drools /></PageWrapper>} />
          <Route path="snaps" element={<PageWrapper><Snaps /></PageWrapper>} />
          <Route path="feed" element={<PageWrapper><FeedPage /></PageWrapper>} />
          <Route path="pics" element={<PageWrapper><Pics /></PageWrapper>} />
          <Route path="drool/:id" element={<PageWrapper><Drool /></PageWrapper>} />
          <Route path="account/settings" element={<PageWrapper><AccountSettings /></PageWrapper>} />
          <Route path="drips" element={<PageWrapper><DripsPage /></PageWrapper>} />
          <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
};

const AppContent = () => {
  // Handle OAuth deep link redirect from system browser (native app only)
  useEffect(() => {
    let handled = false;

    const handleAuthUrl = async (url: string) => {
      if (handled || !url.includes('auth/callback')) return;
      handled = true;

      console.log('[AUTH] Deep link received:', url);

      // Extract just the code — safer than passing the full custom-scheme URL
      let code: string | null = null;
      try {
        code = new URL(url).searchParams.get('code');
      } catch {
        toast.error('Sign in failed: invalid redirect URL');
        await Browser.close();
        return;
      }

      if (!code) {
        toast.error('Sign in failed: no code in redirect');
        console.error('[AUTH] No code found in URL:', url);
        await Browser.close();
        return;
      }

      const { error } = await supabase.auth.exchangeCodeForSession(code);
      await Browser.close();

      if (error) {
        toast.error(`Sign in failed: ${error.message}`);
        console.error('[AUTH] exchangeCodeForSession failed', error);
      }
    };

    // Case 1: app was already running in background, brought to foreground by deep link
    const listenerPromise = CapacitorApp.addListener('appUrlOpen', ({ url }) => handleAuthUrl(url));

    // Case 2: app was killed and freshly launched by the deep link
    CapacitorApp.getLaunchUrl().then((result) => {
      if (result?.url) handleAuthUrl(result.url);
    });

    return () => {
      listenerPromise.then(l => l.remove());
    };
  }, []);

  return (
    <Router>
      <DishSheetProvider>
      <DishStoryProvider>
      <RouteChangeTracker />
      <Suspense
        fallback={
          <div className="flex items-center justify-center h-screen w-screen bg-background">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#cfef00] animate-pulse" />
              <p className="text-[#cfef00] text-sm font-medium">Loading...</p>
            </div>
          </div>
        }
      >
        <AnimatedRoutes />
      </Suspense>
      <Toaster />
      <SonnerToaster />
      <Analytics />
      <SpeedInsights />
      </DishStoryProvider>
      </DishSheetProvider>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LocationProvider>
            <TourProvider>
              <FeedProvider>
                <AppContent />
              </FeedProvider>
            </TourProvider>
          </LocationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
